import React from 'react';
import {Formik, FormikProps} from 'formik';
import {useDispatch} from 'react-redux';

import {errorMessage, flashMessage} from 'shared/actions/notifications';
import {saveCourses} from 'portfolio/actions/courses';
import {Grade, GradeType, Institution} from 'src/types/store';

export interface CourseForm {
  _id: string;
  courseType: string;
  name: string;
  subject: string;
  institution: Pick<Institution, 'name' | 'parentId' | 'country' | 'type' | '_id'>;
  schoolYear: string;
  credits: string;
  gradeType: GradeType;
  hasFinalGrade: boolean;
  grades: Grade[];

  // not from the API, FE only
  // periodType instead of periodsPerYear to be able to share grade setting form components
  // between course and institution. gets converted to 0, 2, 3, 4 and sent back as periodsPerYear
  periodType: Institution['gradeSettings']['periodType'];
  new?: boolean;
}

interface CourseErrors {
  courseType?: string;
  institution?: string;
  subject?: string;
}

type CoursesForm = Record<string, CourseForm[]>;

interface Props {
  initialValues: CoursesForm;
  children: (props: FormikProps<CoursesForm>) => React.ReactNode;
  onSuccess?: () => void;
}

const CourseFormWrapper = ({initialValues, children, onSuccess}: Props) => {
  const dispatch = useDispatch();

  return (
    <Formik
      initialValues={initialValues}
      validate={(values) => {
        const errors: Record<string, Record<string, CourseErrors>> = {};

        Object.keys(values).forEach((key) => {
          values[key].forEach((course, i) => {
            if (course.name) {
              const courseErrors: CourseErrors = {};

              if (!course.courseType) {
                courseErrors.courseType = 'Course type is required.';
              }

              if (!course.institution.parentId) {
                courseErrors.institution = 'Institution is required.';
              }

              if (!course.subject) {
                courseErrors.subject = 'Subject is required.';
              }

              if (Object.keys(courseErrors).length > 0) {
                if (!errors[key]) {
                  errors[key] = {};
                }

                errors[key][i] = courseErrors;
              }
            }
          });
        });

        return errors;
      }}
      onSubmit={async (values) => {
        try {
          await dispatch(
            saveCourses(
              Object.values(values)
                .flat()
                .filter((course) => !!course.name)
            )
          );
          dispatch(flashMessage('Saved courses!'));
          onSuccess?.();
        } catch {
          dispatch(errorMessage());
        }
      }}
      enableReinitialize
      component={children}
    />
  );
};

export default CourseFormWrapper;
